import { useCallback, useEffect, useState } from 'react';
import { useNavigation, useRoute } from '@react-navigation/native';
import { useAppDispatch } from '@store/hooks/useAppDispatch';
import { fetchImagesDocuments } from '@reducers/v2/imageHandlingV2/thunks';
import { getNCFileById } from '@reducers/manufacture';
import { getProductionBatchByNCFile, operationsSearch } from '@reducers/search';
import { withAbortableErrorHandler } from '@helpers';
import { KEYS_TAGS, TAG_KEYS } from '@components/imageHandling/common';
import { OperationSearchItem, ProductionBatch } from '@interfaces/search';
import { useHandleMainDetailsScreenNavigation } from '@v2/pages/common/hooks/useHandleMainDetailsScreenNavigation';
import { LevelSubScreen, Perspective } from '@v2/pages/common/enums';
import { NCFileLevelSubScreen } from '@v2/pages/nc-file/details/common/enums/ncFileDetails.enum';
import { BatchDetailsScreen } from '@v2/pages/batch/details/common/enums/batchDetails.enum';
import { JobDetailsScreen } from '@v2/pages/job/common/enums/jobDetails.enum';
import { PartDetailsScreen } from '@v2/pages/part/details/common/enums/partDetails.enum';
import { ProductionOrderDetailsScreen } from '@v2/pages/productionOrder/common/enums/productionOrderDetails.enum';
import { TraceabilityEntitySummary, TraceabilityNcFile } from '@v2/pages/common/components/TraceabilityGraph/types';
import {
  DETAILS_SCREEN_NAMES,
  NC_FILE_SUB_SCREEN_SUFFIX_REGEXP,
  TRACEABILITY_BATCH_LIMIT,
  TRACEABILITY_BATCH_SELECT,
} from '@v2/pages/common/components/NCFileTraceabilityContent/constants';
import type { NCFileTraceabilityContentProps } from '@v2/pages/common/components/NCFileTraceabilityContent/NCFileTraceabilityContent';

// Perspective-agnostic loader for the NC-file traceability graph. Given a single anchor
// `ncFileId`, it fetches everything the graph needs by id, so it works identically no
// matter which detail perspective (NC File, Batch, Part, Job, Order, Tools) hosts it.
export const useManageNCFileTraceabilityContent = ({ ncFileId, resolving }: NCFileTraceabilityContentProps) => {
  const dispatch = useAppDispatch();
  const navigation = useNavigation<any>();
  const route = useRoute<any>();
  const { navigateToMainDetailsScreen } = useHandleMainDetailsScreenNavigation();

  const [ncFile, setNcFile] = useState<TraceabilityNcFile | null>(null);
  const [ncFileFetched, setNcFileFetched] = useState(false);
  const [jobs, setJobs] = useState<OperationSearchItem[]>([]);
  const [jobsLoaded, setJobsLoaded] = useState(false);
  const [batches, setBatches] = useState<ProductionBatch[]>([]);
  const [batchesLoading, setBatchesLoading] = useState(false);
  const [batchesTotalCount, setBatchesTotalCount] = useState<number | null>(null);
  const [documentCount, setDocumentCount] = useState<number | null>(null);
  const [documentsLoading, setDocumentsLoading] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;

    if (!ncFileId) {
      setNcFile(null);
      setNcFileFetched(false);
      setJobs([]);
      setJobsLoaded(false);
      setBatches([]);
      setBatchesTotalCount(null);
      setBatchesLoading(false);
      setDocumentCount(null);
      setDocumentsLoading(false);
      return;
    }

    const loadGraphData = () => {
      // Reset to a clean loading state so a previous anchor's data never bleeds through.
      setNcFile(null);
      setNcFileFetched(false);
      setJobsLoaded(false);
      setBatchesLoading(true);
      setDocumentsLoading(true);

      // Fire every request concurrently — none depends on another's result. withAbortableErrorHandler
      // gates each onSuccess on the shared signal, so a superseded anchor or unmount writes nothing.
      withAbortableErrorHandler({
        promise: dispatch(getNCFileById(ncFileId, undefined, signal)),
        signal,
        errorMessage: 'Error loading NC file',
        onSuccess: (ncFileData) => {
          setNcFile(ncFileData ?? null);
          setNcFileFetched(true);
        },
      });

      withAbortableErrorHandler({
        promise: dispatch(
          operationsSearch({
            data: {},
            filter: { oDataFilter: `LinkedNcFiles/any(link: link/NcFileId eq ${ncFileId})`, orderBy: ['ModifiedOn desc'] },
            select: null,
            resetHistory: true,
            ignoreHistory: true,
          })
        ),
        signal,
        errorMessage: 'Error loading linked jobs',
        onSuccess: (jobsResult) => {
          setJobs(jobsResult?.searchResult ?? []);
          setJobsLoaded(true);
        },
      });

      withAbortableErrorHandler({
        promise: dispatch(
          getProductionBatchByNCFile({
            id: ncFileId,
            filters: { top: TRACEABILITY_BATCH_LIMIT },
            select: TRACEABILITY_BATCH_SELECT,
          })
        ),
        signal,
        errorMessage: 'Error loading production batches',
        onSuccess: (batchesData) => {
          setBatches(batchesData?.searchResult ?? []);
          setBatchesTotalCount(batchesData?.totalResultCount ?? null);
          setBatchesLoading(false);
        },
      });

      withAbortableErrorHandler({
        promise: dispatch(
          fetchImagesDocuments({
            scopeId: KEYS_TAGS.NcFile as keyof typeof TAG_KEYS,
            scopeReferenceId: ncFileId,
            retrieveAllDocuments: false,
          })
        ),
        signal,
        errorMessage: 'Error loading documents',
        onSuccess: (documentsResult) => {
          setDocumentCount(fetchImagesDocuments.fulfilled.match(documentsResult) ? documentsResult.payload.data?.length ?? 0 : 0);
          setDocumentsLoading(false);
        },
      });
    };

    loadGraphData();

    return () => {
      controller.abort();
    };
  }, [ncFileId, dispatch]);

  const openPart = useCallback(
    (entity: TraceabilityEntitySummary) => {
      navigateToMainDetailsScreen({
        mainScreen: DETAILS_SCREEN_NAMES.part,
        subScreen: PartDetailsScreen.PART,
        levelSubScreen: LevelSubScreen.GENERAL,
        params: {
          perspective: Perspective.PART,
          partId: entity.id,
          partName: entity.title,
          selectedImageKey: entity.imageHash,
          documentId: entity.imageDocumentId,
        },
      });
    },
    [navigateToMainDetailsScreen]
  );

  const openProductionOrder = useCallback(
    (entity: TraceabilityEntitySummary) => {
      navigateToMainDetailsScreen({
        mainScreen: DETAILS_SCREEN_NAMES.productionOrder,
        subScreen: ProductionOrderDetailsScreen.ORDER,
        levelSubScreen: LevelSubScreen.GENERAL,
        params: {
          perspective: Perspective.ORDER,
          productionOrderId: entity.id,
          selectedImageKey: entity.imageHash,
          documentId: entity.imageDocumentId,
        },
      });
    },
    [navigateToMainDetailsScreen]
  );

  const openJob = useCallback(
    (entity: TraceabilityEntitySummary) => {
      navigateToMainDetailsScreen({
        mainScreen: DETAILS_SCREEN_NAMES.job,
        subScreen: JobDetailsScreen.JOBS,
        levelSubScreen: LevelSubScreen.GENERAL,
        params: {
          perspective: Perspective.JOBS,
          productionOperationId: entity.id,
          selectedImageKey: entity.imageHash,
          documentId: entity.imageDocumentId,
        },
      });
    },
    [navigateToMainDetailsScreen]
  );

  const openBatch = useCallback(
    (entity: TraceabilityEntitySummary) => {
      navigateToMainDetailsScreen({
        mainScreen: DETAILS_SCREEN_NAMES.batch,
        subScreen: BatchDetailsScreen.BATCH,
        levelSubScreen: LevelSubScreen.GENERAL,
        params: {
          perspective: Perspective.BATCH,
          batchId: entity.id,
          batchNumber: entity.title,
          ncFileStatus: entity.ncFileStatus,
          selectedImageKey: entity.imageHash,
          documentId: entity.imageDocumentId,
        },
      });
    },
    [navigateToMainDetailsScreen]
  );

  const openNcFileSubScreen = useCallback(
    (subScreen: string) => {
      const nextSubScreen = subScreen || NCFileLevelSubScreen.GENERAL;
      const currentLevelScreenName = route.name?.endsWith(NCFileLevelSubScreen.TRACEABILITY)
        ? route.name.slice(0, -NCFileLevelSubScreen.TRACEABILITY.length)
        : route.name?.replace(NC_FILE_SUB_SCREEN_SUFFIX_REGEXP, '') || '';

      navigation.navigate(`${currentLevelScreenName}${nextSubScreen}`, {
        ...route.params,
        perspective: route.params?.perspective ?? Perspective.NC_FILE,
      });
    },
    [navigation, route.name, route.params]
  );

  return {
    ncFile,
    // While the host perspective is still resolving its anchor id we report "not loaded"
    // so the graph shows a spinner instead of flashing the empty state.
    ncFileLoaded: ncFileId ? ncFileFetched : !resolving,
    jobs,
    jobsLoaded,
    batches,
    batchesLoading,
    batchesTotalCount,
    documentCount,
    documentsLoading,
    openPart,
    openProductionOrder,
    openJob,
    openBatch,
    openNcFileSubScreen,
  };
};
